import IndexedDBService from './indexedDBService';

/**
 * POS Order ID Database Service
 * Manages sequential counters for posOrderId generation in IndexedDB
 */
class PosOrderIdDB extends IndexedDBService {
  constructor() {
    super('posOrderIdDB', 1);
  }

  setupObjectStores(db) {
    // Counters Store - stores sequential numbers per store/date combination
    if (!db.objectStoreNames.contains('counters')) {
      const countersStore = db.createObjectStore('counters', {
        keyPath: 'key',
      });
      countersStore.createIndex('storeId', 'storeId', { unique: false });
      countersStore.createIndex('dateCode', 'dateCode', { unique: false });
      countersStore.createIndex('timestamp', 'timestamp', { unique: false });
    }
  }

  /**
   * Get counter value for a given key
   * @param {string} key - Format: posOrderId_${storeId}_${dateCode}
   * @returns {Promise<number>} Current counter value (0 if not found)
   */
  async getCounter(key) {
    try {
      const result = await this.get('counters', key);
      return result ? result.value : 0;
    } catch (error) {
      // If key doesn't exist, return 0
      if (error.name === 'DataError' || error.name === 'NotFoundError') {
        return 0;
      }
      throw error;
    }
  }

  /**
   * Set counter value for a given key (atomic operation)
   * @param {string} key - Format: posOrderId_${storeId}_${dateCode}
   * @param {number} value - Counter value to set
   * @returns {Promise<void>}
   */
  async setCounter(key, value) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['counters'], 'readwrite');
      const store = transaction.objectStore('counters');

      // Parse key to extract storeId and dateCode for indexing
      const parts = key.split('_');
      const storeId = parts.length > 1 ? parts[1] : '';
      const dateCode = parts.length > 2 ? parts.slice(2).join('_') : '';

      const data = {
        key: key,
        storeId: storeId,
        dateCode: dateCode,
        value: value,
        timestamp: Date.now(),
      };

      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Decrement counter if it matches the current value (pruning)
   * @param {string} key - Format: posOrderId_${storeId}_${dateCode}
   * @param {number} valueToPrune - The sequential number to compare against
   */
  async decrementCounter(key, valueToPrune) {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['counters'], 'readwrite');
      const store = transaction.objectStore('counters');

      const request = store.get(key);
      request.onsuccess = async () => {
        const result = request.result;
        if (result && result.value === valueToPrune) {
          console.log(`[PosOrderIdDB] Pruning counter ${key}: ${valueToPrune} -> ${valueToPrune - 1}`);
          result.value = Math.max(0, valueToPrune - 1);
          result.timestamp = Date.now();
          const updateRequest = store.put(result);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          console.log(`[PosOrderIdDB] Skip pruning ${key}: Current ${result?.value} != ${valueToPrune}`);
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
const posOrderIdDB = new PosOrderIdDB();
export default posOrderIdDB;

